import { useCallback, useState } from 'react';
import { cursorActionFor, steppedIndex } from './cursor';
import type { BandScale } from './scale';

/**
 * One cursor per chart, driven by pointer or by keyboard.
 *
 * The obvious implementation — one focusable element per data point — makes a
 * sixty-point chart into sixty tab stops, and a page with four charts into
 * two hundred and forty. Nobody tabs through that; they tab *past* it, which
 * is the same as the chart not being keyboard-accessible at all.
 *
 * So there is a single focusable cursor, and the arrow keys move it. That is
 * the roving-tabindex idea `Segmented` and `Tabs` already use for their
 * options, applied to data.
 *
 * `mode` matters as much as `index`. A pointer hover must not announce
 * anything — a sighted mouse user reading a tooltip does not want their screen
 * reader narrating every pixel of travel — so the live region only speaks when
 * the cursor was moved by a key.
 */

type CursorMode = 'pointer' | 'keyboard' | 'idle';

export interface ChartCursor {
  /** Which category is active, or undefined when none is. */
  key: string | undefined;
  index: number;
  mode: CursorMode;
  onPointerMove: (event: React.PointerEvent<SVGRectElement>) => void;
  onPointerLeave: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  /** Clear without waiting for blur — used by Escape. */
  dismiss: () => void;
}

export function useChartCursor(
  scale: BandScale,
  /** Plot-space x offset, so pointer coordinates land in the same space. */
  offsetX: number,
  /** Called on Enter, when the active datum drills through. */
  onActivate?: (key: string) => void,
): ChartCursor {
  const [index, setIndex] = useState(-1);
  const [mode, setMode] = useState<CursorMode>('idle');
  const keys = scale.keys;

  const onPointerMove = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      // `getBoundingClientRect` on the hit rect rather than the SVG: the SVG
      // may be scaled by its viewBox, and the rect is in the same user space as
      // the scale.
      const box = event.currentTarget.getBoundingClientRect();
      const key = scale.nearest(event.clientX - box.left + offsetX);
      if (key === undefined) return;

      setIndex(keys.indexOf(key));
      setMode('pointer');
    },
    [scale, offsetX, keys],
  );

  const onPointerLeave = useCallback(() => {
    // Only the pointer's own cursor is cleared. If the chart also has keyboard
    // focus, moving the mouse away must not steal the focused reader's place.
    setMode((current) => (current === 'pointer' ? 'idle' : current));
    setIndex((current) => (mode === 'pointer' ? -1 : current));
  }, [mode]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (keys.length === 0) return;

      const action = cursorActionFor(event.key);
      switch (action.kind) {
        case 'step':
          event.preventDefault();
          setMode('keyboard');
          return setIndex((current) => steppedIndex(current, action.delta, keys.length));
        case 'jump':
          event.preventDefault();
          setMode('keyboard');
          return setIndex(action.to === 'first' ? 0 : keys.length - 1);
        case 'dismiss':
          setMode('idle');
          return setIndex(-1);
        case 'activate': {
          const key = keys[index];
          if (key !== undefined && onActivate) {
            event.preventDefault();
            onActivate(key);
          }
          return;
        }
        default:
          return;
      }
    },
    [keys, index, onActivate],
  );

  const onFocus = useCallback(() => {
    // Land on the most recent reading rather than the oldest. "How are we
    // doing" is a question about the right-hand end of the chart.
    setMode('keyboard');
    setIndex((current) => (current < 0 ? keys.length - 1 : current));
  }, [keys.length]);

  // Blur and Escape-style dismissal clear the cursor the same way.
  const dismiss = useCallback(() => {
    setMode('idle');
    setIndex(-1);
  }, []);

  return {
    key: index >= 0 ? keys[index] : undefined,
    index,
    mode,
    onPointerMove,
    onPointerLeave,
    onKeyDown,
    onFocus,
    onBlur: dismiss,
    dismiss,
  };
}
